/* ================================================================
   Mor & Meadow — Lenis smooth-scroll bootstrap.

   Mirrors the Next.js `SmoothScrollProvider` so the Shopify storefront
   feels identical to morandmeadow.com:
     - 1.4s exponential ease-out (matches the React reference exactly)
     - Disabled when `prefers-reduced-motion: reduce` is set
     - Anchor links (`a[href^="#"]`) are intercepted to scroll smoothly
       with a 96px offset for the fixed header
     - Exposes `window.__lenis` so sections (process-cards, framework,
       …) can opt into smooth `lenis.scrollTo(target, { duration })`
       calls instead of native `window.scrollTo`

   Lenis itself is loaded earlier in <head> as a vendored asset via:
     {{ 'lenis.min.js' | asset_url }}
   Both scripts use `defer` so they execute in document order. The
   short setTimeout retry below is a defensive fallback for any
   environment where the global `window.Lenis` hasn't appeared yet.
   ================================================================ */
(function () {
  if (window.__mamLenisInit) return;
  window.__mamLenisInit = true;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function init(retries) {
    if (window.__lenis) return;
    /* Skip on reduced-motion preference. Lenis is intentionally tied
       to user motion settings — falling back to native scrolling. */
    if (window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    if (typeof window.Lenis !== 'function') {
      /* CDN delayed — retry up to ~2s. After that, give up gracefully
         and the site falls back to native smooth scrolling. */
      if (retries === undefined) retries = 0;
      if (retries > 20) return;
      setTimeout(function () { init(retries + 1); }, 100);
      return;
    }

    var lenis = new window.Lenis({
      duration: 1.4,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      touchMultiplier: 1,
      /* Panels that scroll on their own (the basket drawer, anything marked
         data-lenis-prevent) keep native wheel/touch scrolling. */
      prevent: function (node) { return node.id === 'CartDrawer' || node.tagName === 'CART-DRAWER'; }
    });
    window.__lenis = lenis;

    /* Pause the page's smooth scroll while the basket drawer is open, so the
       page behind it stays put (the theme's body.overflow-hidden lock doesn't
       reach Lenis, which scrolls the document). */
    function watchDrawer() {
      var drawer = document.getElementById('CartDrawer');
      if (!drawer || drawer.__mamLenisWatch) return;
      drawer.__mamLenisWatch = true;
      function sync() {
        var open = drawer.classList.contains('active') || drawer.classList.contains('show');
        if (open) lenis.stop(); else lenis.start();
      }
      new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ['class'] });
      sync();
    }
    watchDrawer();

    function tick(time) {
      lenis.raf(time);
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);

    /* Anchor link interception — same contract as the React version.
       Lenis handles the scroll, the 96px offset clears the fixed
       header. */
    var ANCHOR_OFFSET = 96;
    function findAnchor(hash) {
      if (!hash || hash.length < 2) return null;
      try { return document.getElementById(decodeURIComponent(hash.slice(1))); } catch (err) { return null; }
    }
    function scrollToAnchor(el) {
      lenis.scrollTo(el, {
        offset: -ANCHOR_OFFSET,
        /* Sections above can change height during the scroll (lazy images,
           review widgets loading), so settle on the target once more. */
        onComplete: function () {
          if (Math.abs(el.getBoundingClientRect().top - ANCHOR_OFFSET) > 2) {
            lenis.scrollTo(el, { offset: -ANCHOR_OFFSET, duration: 0.6 });
          }
        }
      });
    }
    document.addEventListener('click', function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var link = target.closest('a[href^="#"]');
      if (!link) return;
      var href = link.getAttribute('href');
      if (!href || href === '#') return;
      /* Only handle plain in-page hash links — not tab triggers etc. */
      var el = findAnchor(href);
      if (!el) return;
      e.preventDefault();
      scrollToAnchor(el);
    });

    /* Arriving with a hash (e.g. /products/x#reviews): the browser jumps
       before widgets above the target have loaded, so keep the target in
       place while the page settles, until the visitor scrolls themselves. */
    var hashTarget = findAnchor(window.location.hash);
    if (hashTarget && window.ResizeObserver) {
      var userMoved = false;
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(function (type) {
        window.addEventListener(type, function () { userMoved = true; }, { once: true, passive: true });
      });
      var pin = function () {
        if (userMoved) return;
        lenis.scrollTo(hashTarget, { offset: -ANCHOR_OFFSET, immediate: true, force: true });
      };
      var ro = new ResizeObserver(pin);
      ro.observe(document.body);
      pin();
      setTimeout(function () { ro.disconnect(); }, 8000);
    }
  }

  ready(function () { init(0); });
})();
